import React, { useCallback, useEffect, useState } from "react";
import { Box, Button, Card, Grid, Paper, Typography } from "@mui/material";
import axios from "../../api";
import { useNavigate, useParams } from "react-router-dom";
import { useSnackbar } from "components/AlertMessages/SnackbarContext";
import { useCustomerId } from "context/CustomerIdContext";
import SoftBox from "components/SoftBox";
import { AxiosError } from "axios";
import { EmailOutlined, LocationOn } from "@mui/icons-material";
import PhoneIcon from "@mui/icons-material/Phone";
import { LucideGitBranchPlus } from "lucide-react";

interface Members {
  name: string;
  email: string;
  phone: string;
  address: string;
  is_active: number;
  employee_id: number;
  state_name: string;
  city_name: string;
  region_name: string;
  role_name: string;
  divisions: string;
}

interface ViewMembersProps {
  /** When provided, "Add Member" closes current overlay and parent opens Add Member overlay (no navigation) */
  onOpenAddMember?: (customerId: number) => void;
  /** Increment to refetch the list without changing customer id */
  refreshNonce?: number;
}

const ViewMembers = ({
  onOpenAddMember,
  refreshNonce = 0,
}: ViewMembersProps = {}) => {
  const { fetchError } = useSnackbar();
  const contextId = useCustomerId();
  const { id: paramId } = useParams();
  const id = contextId ?? paramId;
  const navigate = useNavigate();

  const [members, setMembers] = useState<Members[] | null>(null);

  const fetchMembers = useCallback(async () => {
    try {
      const response = await axios.get(`/employee/customer/${id}`);
      setMembers(response?.data);
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        fetchError(error.response.data.error);
      }
    }
  }, [id]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers, refreshNonce]);

  return (
    <Card sx={{ p: 3, maxWidth: "100%", background: "#fff" }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h5" fontWeight="600">
          Manage Members
        </Typography>

        <Button
          variant="contained"
          sx={{
            backgroundColor: "#4aa4a4",
            textTransform: "none",
            borderRadius: "8px",
            px: 3,
            py: 1,
            "&:hover": { backgroundColor: "#3c8f8f" },
          }}
          onClick={() => {
            if (onOpenAddMember && id) {
              onOpenAddMember(Number(id));
            } else {
              navigate(`/add-employee`);
            }
          }}
        >
          Add Member
        </Button>
      </Box>
      <SoftBox mt={2}>
        <Grid container justifyContent="center">
          <Grid item xs={12} md={12}>
            {members && members.length > 0 ? (
              members?.map((data) => {
                return (
                  <Paper
                    key={data.employee_id}
                    elevation={0}
                    sx={{
                      p: 3,
                      mb: 3,
                      borderRadius: "18px",
                      border: "1px solid #E6E6E6",
                      backgroundColor: "#FFFFFF",
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 2.5,
                      minHeight: "140px",
                    }}
                  >
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: "50%",
                        backgroundColor: "#D1F5D8",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 18,
                        fontWeight: 700,
                        color: "#16823C",
                        textTransform: "uppercase",
                      }}
                    >
                      {data.name
                        .split(" ")
                        .map((n) => n[0])
                        .join("")}
                    </Box>
                    <Box flex={1}>
                      <Typography
                        fontSize={18}
                        fontWeight={700}
                        color="#222"
                        sx={{ mb: 0.3 }}
                      >
                        {data.name}
                      </Typography>

                      <Typography
                        fontSize={14}
                        fontWeight={500}
                        color="#6B6B6B"
                        mb={1.5}
                      >
                        {data.role_name}
                      </Typography>
                      <Box display="flex" alignItems="center" mb={0.6} gap={1}>
                        <EmailOutlined />
                        <Typography fontSize={14}>{data.email}</Typography>
                      </Box>

                      <Box display="flex" alignItems="center" mb={0.6} gap={1}>
                        <PhoneIcon />
                        <Typography fontSize={14}>{data.phone}</Typography>
                      </Box>

                      <Box display="flex" alignItems="center" gap={1}>
                        <LocationOn />
                        <Typography fontSize={14}>
                          {data.address},{data.region_name},{data.city_name},
                          {data.state_name}
                        </Typography>
                      </Box>
                      <Box display="flex" alignItems="center" gap={1}>
                        <LucideGitBranchPlus />
                        <Typography fontSize={14}>{data.divisions}</Typography>
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-end",
                        gap: 1.2,
                      }}
                    >
                      <Box
                        sx={{
                          px: 1.6,
                          py: 0.5,
                          borderRadius: "20px",
                          fontSize: "12px",
                          fontWeight: 600,
                          textTransform: "capitalize",
                          backgroundColor:
                            data.is_active === 1 ? "#D9F4E5" : "#ECECEC",
                          color: data.is_active === 1 ? "#0A7A3D" : "#555",
                        }}
                      >
                        {data.is_active === 1 ? "active" : "inactive"}
                      </Box>
                    </Box>
                  </Paper>
                );
              })
            ) : (
              <Typography
                fontSize={15}
                fontWeight={600}
                color="#777"
                textAlign="center"
                mt={4}
              >
                No Members found
              </Typography>
            )}
          </Grid>
        </Grid>
      </SoftBox>
    </Card>
  );
};

export default ViewMembers;
